import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import Loader from '../components/Loader.jsx';
import { fetchTransactionsAndSummary, saveUpdatedTransaction, deleteTransaction } from '../lib/Log.js';
import '../styles/TransactionLog.css';

const TransactionLog = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [balance, setBalance] = useState(0);
    const [userName, setUserName] = useState('');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [summary, setSummary] = useState({
        allDebits: 0,
        allCredits: 0,
        totalTransactions: 0,
    });
    const [buttonLoading, setButtonLoading] = useState({
        save: false,
        delete: false,
        cancel: false,
    });

    // Fetch user and transaction data
    useEffect(() => {
        const loadData = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'online', userId));
                if (!userDoc.exists()) {
                    throw new Error('User not found');
                }

                const userData = userDoc.data();
                // Set user name of user with details
                setUserName(userData.fullName || '');

                const { fetchedTransactions, calculatedBalance, summary } = await fetchTransactionsAndSummary(userId);
                setTransactions(fetchedTransactions);
                setBalance(calculatedBalance);
                setSummary(summary);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [userId]);

    // view each transaction item
    const handleViewClick = (index) => {
        setSelectedTransaction(transactions[index]);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setSelectedTransaction((prev) => ({
            ...prev,
            [name]: name === 'amount' ? parseFloat(value) : value,
        }));
    };


    const handleEditSave = async () => {
        setButtonLoading((prev) => ({ ...prev, save: true }));
        try {
            const updatedTransactions = await saveUpdatedTransaction(userId, transactions, selectedTransaction);
            setTransactions(updatedTransactions);

            const { calculatedBalance, summary } = await fetchTransactionsAndSummary(userId);
            setBalance(calculatedBalance);
            setSummary(summary);

            setSelectedTransaction(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setButtonLoading((prev) => ({ ...prev, save: false }));
        }
    };


    const handleDeleteClick = async () => {
        setButtonLoading((prev) => ({ ...prev, delete: true }));
        try {
            const updatedTransactions = await deleteTransaction(userId, transactions, selectedTransaction);
            setTransactions(updatedTransactions);

            const { calculatedBalance, summary } = await fetchTransactionsAndSummary(userId);
            setBalance(calculatedBalance);
            setSummary(summary);

            setSelectedTransaction(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setButtonLoading((prev) => ({ ...prev, delete: false }));
        }
    };


    const handleCancelEdit = () => {
        setSelectedTransaction(null);
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleAddTransactionClick = () => {
        navigate('/userdetail/add-transaction');
    };

    if (loading) return <Loader />;
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className="transaction-log">
            <h2>Transaction Log for {userName}</h2>

            {/* Amount and Add Transactions */}
            <div className="balance-container">
                <h5>Total Balance: <span id="balance">${balance.toFixed(2)}</span></h5>
                <button onClick={handleAddTransactionClick} className="action-button">
                    Add Transaction
                </button>
            </div>

            {/* Account summary: Total, Credits, & Debits */}
            <div className="summary-boxed">
                <div className="summaryBoxes">
                    <h3>Total Transactions</h3>
                    <p>{summary.totalTransactions || 'Loading ...'}</p>
                </div>
                <div className="summaryBoxes">
                    <h3>All Debits</h3>
                    <p>${summary.allDebits.toFixed(2)}</p>
                </div>
                <div className="summaryBoxes">
                    <h3>All Credits</h3>
                    <p>${summary.allCredits.toFixed(2)}</p>
                </div>
            </div>


            {/* Table of Transaction */}
            <table>
                <thead>
                    <tr>
                        <th>Amount</th>
                        <th className="noShow">Description</th>
                        <th>Date</th>
                    </tr>
                </thead>
                <tbody>
                    {transactions.map((transaction, index) => (
                        <tr key={index} onClick={() => handleViewClick(index)}>
                            <td>
                                {transaction.type === 'credit' && (
                                    <i className="fas fa-circle-plus text-success"></i>
                                )}
                                {transaction.type === 'debit' && (
                                    <i className="fas fa-circle-minus text-danger"></i>
                                )}
                                ${transaction.amount.toFixed(2)}
                            </td>
                            <td className="noShow">{transaction.description}</td>
                            <td>{moment(transaction.date).format('MMMM D, YYYY')}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {selectedTransaction && (
                <>
                    <div
                        className={`nav-overlay ${selectedTransaction ? 'visible' : ''}`}
                        onClick={handleCancelEdit}></div>
                    <div className={`side-bar ${selectedTransaction ? 'open' : ''}`}>

                        <div className="side-bar-content">
                            <span className="close-button" onClick={handleCancelEdit}>&times;</span>
                            <div className="logTitle hero-title">
                                ${selectedTransaction.amount} {selectedTransaction.type}ed on {moment(selectedTransaction.date).format('MMMM D, YYYY')}
                            </div>


                            {/* View and Edit Description */}
                            <div>
                                <label className='labels'>Description:</label>
                                <input
                                    type="text"
                                    className='input-field'
                                    name="description"
                                    value={selectedTransaction.description}
                                    onChange={handleEditChange}
                                />
                            </div>

                            {/* View and Edit Beneficiary */}
                            <div>
                                <label className='labels'>Beneficiary:</label>
                                <input
                                    type="text"
                                    name="beneficiary"
                                    className='input-field'
                                    value={selectedTransaction.beneficiary || ''}
                                    onChange={handleEditChange}
                                />
                            </div>

                            {/* View and Edit Type */}
                            <div>
                                <label className='labels'>Type:</label>
                                <select
                                    name="type"
                                    value={selectedTransaction.type}
                                    onChange={handleEditChange}
                                    className='input-field'
                                >
                                    <option value="credit">Credit</option>
                                    <option value="debit">Debit</option>
                                </select>
                            </div>

                            {/* View and Edit Account */}
                            <div>
                                <label className='labels'>Account:</label>
                                <select
                                    name="account"
                                    value={selectedTransaction.account}
                                    className='input-field'
                                    onChange={handleEditChange}
                                >
                                    <option value="Savings">Savings</option>
                                    <option value="Investments">Investments</option>
                                    <option value="Trading">Trading</option>
                                    <option value="Loans">Loans</option>
                                </select>
                            </div>

                             {/* View and Edit Stage */}
                             <div>
                                <label className='labels'>Stage:</label>
                                <select
                                    name="stage"
                                    value={selectedTransaction.stage}
                                    onChange={handleEditChange}
                                    className='input-field'
                                >
                                    <option value="complete">Complete</option>
                                    <option value="pending">Pending</option>
                                    <option value="active">Active</option>
                                </select>
                            </div>


                            <div className="sidebar-actions">
                                <button onClick={handleEditSave} className="main-button">
                                    {buttonLoading.save ? 'Saving...' : 'Save'}
                                </button>
                                <button onClick={handleDeleteClick} className="main-button">
                                    {buttonLoading.delete ? 'Deleting...' : 'Delete'}
                                </button>
                                <button onClick={handleCancelEdit} className="main-button">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className="center">
                <button onClick={handleGoBack} className="action-button">Go Back</button>
            </div>
        </div>
    );
};

export default TransactionLog;
